import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  listMarketingTemplates,
  createMarketingTemplate,
  updateMarketingTemplate,
  deleteMarketingTemplate,
  duplicateMarketingTemplate,
} from "../../api/marketingCampaigns";
import SBINModal from "../../components/modals/SBINModal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import InfoTooltip from "../../components/common/InfoTooltip";
import SafeHtml from "../../components/common/SafeHtml";
import VisualTemplateBodyEditor, {
  sanitizeTemplateEditorHtml,
  templateHtmlToText,
} from "../../components/marketing/VisualTemplateBodyEditor";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { CAMPAIGN_GOALS, CAMPAIGN_TOOLTIPS } from "../../constants/marketingCampaigns";
import "../MarketingSectionCompact.css";

const TEMPLATE_VARIABLES = [
  "{{first_name}}",
  "{{salon_name}}",
  "{{salon_phone}}",
  "{{salon_address}}",
  "{{booking_url}}",
  "{{unsubscribe_url}}",
  "{{promo_code}}",
  "{{campaign_link}}",
];

const DEFAULT_FORM = {
  name: "",
  goal: CAMPAIGN_GOALS[0]?.value || "",
  subject: "",
  preheader: "",
  body_html: "",
  body_text: "",
  cta_label: "",
  cta_url: "",
  attribution_window_days: 14,
  include_promo_code: false,
  include_share_snippet: false,
};

const EDITOR_MODES = {
  CREATE: "create",
  EDIT_LOCAL: "edit_local",
  PREVIEW_GLOBAL: "preview_global",
  CUSTOMIZE_GLOBAL: "customize_global",
};

const isGlobalTemplate = (template) => template?.salon_id == null;

const buildCustomTemplateName = (name) => {
  const baseName = String(name || "Campaign Template").trim();
  return /\(custom\)$/i.test(baseName) ? baseName : `${baseName} (Custom)`;
};

export default function CampaignTemplates() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editing, setEditing] = useState(null);
  const [editorMode, setEditorMode] = useState(EDITOR_MODES.CREATE);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [textFallbackEdited, setTextFallbackEdited] = useState(false);
  const isPreviewMode = editorMode === EDITOR_MODES.PREVIEW_GLOBAL;
  const isCustomizeMode = editorMode === EDITOR_MODES.CUSTOMIZE_GLOBAL;

  const loadTemplates = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const rows = await listMarketingTemplates(salonId, token);
      setTemplates(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load templates.");
    } finally {
      setLoading(false);
    }
  }, [salonId, token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (!search) return templates;
    const needle = search.toLowerCase();
    return templates.filter((template) => {
      return (
        template.name?.toLowerCase().includes(needle) ||
        template.subject?.toLowerCase().includes(needle)
      );
    });
  }, [templates, search]);

  const groupedTemplates = useMemo(() => {
    const groups = new Map();
    CAMPAIGN_GOALS.forEach((goal) => {
      groups.set(goal.value, []);
    });
    filteredTemplates.forEach((template) => {
      if (!groups.has(template.goal)) {
        groups.set(template.goal, []);
      }
      groups.get(template.goal).push(template);
    });
    groups.forEach((items) => {
      items.sort((a, b) => {
        const aName = String(a?.name || "");
        const bName = String(b?.name || "");
        const nameCompare = aName.localeCompare(bName);
        if (nameCompare !== 0) return nameCompare;
        if (isGlobalTemplate(a) !== isGlobalTemplate(b)) {
          return isGlobalTemplate(a) ? 1 : -1;
        }
        return Number(a?.id || 0) - Number(b?.id || 0);
      });
    });
    return groups;
  }, [filteredTemplates]);

  const resetEditorState = () => {
    setTextFallbackEdited(false);
  };

  const closeEditor = () => {
    setModalOpen(false);
    resetEditorState();
  };

  const setFormFromTemplate = (template, { customize = false } = {}) => {
    setForm({
      name: customize
        ? buildCustomTemplateName(template.name)
        : template.name || "",
      goal: template.goal || CAMPAIGN_GOALS[0]?.value || "",
      subject: template.subject || "",
      preheader: template.preheader || "",
      body_html: template.body_html || "",
      body_text: template.body_text || "",
      cta_label: template.cta_label || "",
      cta_url: template.cta_url || "",
      attribution_window_days: template.attribution_window_days || 14,
      include_promo_code: Boolean(template.include_promo_code),
      include_share_snippet: Boolean(template.include_share_snippet),
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setEditorMode(EDITOR_MODES.CREATE);
    resetEditorState();
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setFormFromTemplate(template);
    setEditorMode(EDITOR_MODES.EDIT_LOCAL);
    resetEditorState();
    setModalOpen(true);
  };

  const openPreview = (template) => {
    setEditing(template);
    setFormFromTemplate(template);
    setEditorMode(EDITOR_MODES.PREVIEW_GLOBAL);
    resetEditorState();
    setModalOpen(true);
  };

  const openCustomize = (template) => {
    setEditing(template);
    setFormFromTemplate(template, { customize: true });
    setEditorMode(EDITOR_MODES.CUSTOMIZE_GLOBAL);
    resetEditorState();
    setModalOpen(true);
  };

  const updateBodyHtml = useCallback(
    (nextHtml) => {
      const cleanHtml = sanitizeTemplateEditorHtml(nextHtml);
      setForm((prev) => ({
        ...prev,
        body_html: cleanHtml,
        body_text: textFallbackEdited ? prev.body_text : templateHtmlToText(cleanHtml),
      }));
    },
    [textFallbackEdited]
  );

  const handleSave = async () => {
    if (!salonId) return;
    if (isPreviewMode) return;
    const payload = {
      ...form,
      name: (form.name || "").trim(),
      subject: (form.subject || "").trim(),
      body_html: sanitizeTemplateEditorHtml(form.body_html),
      attribution_window_days: Number(form.attribution_window_days) || 14,
    };

    if (!payload.name || !payload.subject || !templateHtmlToText(payload.body_html)) {
      setError("Template name, subject, and message are required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editorMode === EDITOR_MODES.EDIT_LOCAL && editing?.id) {
        await updateMarketingTemplate(editing.id, payload, token);
      } else {
        await createMarketingTemplate(salonId, payload, token);
      }
      closeEditor();
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save template.");
    } finally {
      setSaving(false);
    }
  };

  const modalTitle = isPreviewMode
    ? "Preview default template"
    : isCustomizeMode
      ? "Customize default template"
      : editing
        ? "Edit template"
        : "Create template";

  const saveButtonLabel = saving
    ? "Saving..."
    : isCustomizeMode
      ? "Save Salon Copy"
      : editing
        ? "Save template"
        : "Create template";

  const handleDuplicate = async (template) => {
    if (!salonId) return;
    setError("");
    try {
      await duplicateMarketingTemplate(salonId, template.id, token);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to duplicate template.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError("");
    try {
      await deleteMarketingTemplate(deleteTarget.id, token);
      setDeleteTarget(null);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to delete template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Campaign Templates
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/manager/marketing/campaigns")}
                className="rounded-full border border-white/70 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/10"
              >
                Back to campaigns
              </button>
              <button
                type="button"
                onClick={openCreate}
                className="rounded-full bg-white px-4 py-2 text-xs md:text-sm font-semibold text-rose-600 hover:bg-rose-50"
              >
                New template
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
              <div className="text-sm font-semibold text-slate-900">
                Template variables
              </div>
              <div className="text-xs text-slate-600">
                Use these placeholders in your subject or body: {TEMPLATE_VARIABLES.join(", ")}
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-base md:text-lg font-semibold text-slate-900">
                  Template library
                </div>
                <div className="text-xs md:text-sm text-slate-500">
                  Salon: {salonName || "Unknown"} (ID {salonId || "--"})
                </div>
              </div>
              <input
                type="text"
                placeholder="Search templates"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 w-full max-w-xs rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            {loading && (
              <div className="text-xs text-slate-500">Loading templates...</div>
            )}

            <div className="space-y-6">
              {CAMPAIGN_GOALS.map((goal) => {
                const goalTemplates = groupedTemplates.get(goal.value) || [];
                return (
                  <div key={goal.value} className="space-y-3">
                    <div className="text-sm font-semibold text-slate-900">
                      {goal.label}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {goalTemplates.map((template) => {
                        const templateIsGlobal = isGlobalTemplate(template);
                        return (
                          <div
                            key={template.id}
                            className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-sm font-semibold text-slate-900">
                                    {template.name}
                                  </div>
                                  <span
                                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                      templateIsGlobal
                                        ? "bg-slate-100 text-slate-600"
                                        : "bg-emerald-100 text-emerald-700"
                                    }`}
                                  >
                                    {templateIsGlobal ? "Default" : "Salon template"}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-500">
                                  Subject: {template.subject}
                                </div>
                                {templateIsGlobal && (
                                  <div className="text-xs text-slate-500">
                                    Shared template. Customize it to make a salon copy.
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {templateIsGlobal ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openPreview(template)}
                                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      Preview
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openCustomize(template)}
                                      className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                                    >
                                      Customize
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openEdit(template)}
                                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDuplicate(template)}
                                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      Duplicate
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setDeleteTarget(template)}
                                      className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-slate-500">
                              Attribution window: {template.attribution_window_days} days
                            </div>
                            <SafeHtml
                              className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
                              html={template.body_html}
                            />
                          </div>
                        );
                      })}

                      {goalTemplates.length === 0 && !loading && (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                          No templates yet for this goal.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <SBINModal
        isOpen={modalOpen}
        onClose={closeEditor}
        title={modalTitle}
        size="wide"
        fitContent
      >
        {error && (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        {isPreviewMode && (
          <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            This is a shared default template. Preview it here, or customize it to
            create a salon copy.
          </div>
        )}
        {isCustomizeMode && (
          <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Saving will create a salon copy for {salonName || "this salon"}.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500">Name</label>
              <input
                type="text"
                value={form.name}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">Goal</label>
              <select
                value={form.goal}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, goal: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              >
                {CAMPAIGN_GOALS.map((goal) => (
                  <option key={goal.value} value={goal.value}>
                    {goal.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">Subject</label>
              <input
                type="text"
                value={form.subject}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, subject: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">Preheader</label>
              <input
                type="text"
                value={form.preheader}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, preheader: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">Message</label>
              <div className="mt-1">
                <VisualTemplateBodyEditor
                  value={form.body_html}
                  onChange={updateBodyHtml}
                  variables={TEMPLATE_VARIABLES}
                  readOnly={isPreviewMode}
                />
              </div>
            </div>

            <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Advanced HTML
              </summary>
              <textarea
                rows="5"
                value={form.body_html}
                disabled={isPreviewMode}
                onChange={(event) => updateBodyHtml(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
              />
            </details>

            <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Plain-text fallback
              </summary>
              <textarea
                rows="4"
                value={form.body_text}
                disabled={isPreviewMode}
                onChange={(event) => {
                  setTextFallbackEdited(true);
                  setForm((prev) => ({ ...prev, body_text: event.target.value }));
                }}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                disabled={isPreviewMode}
                className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                onClick={() => {
                  setTextFallbackEdited(false);
                  setForm((prev) => ({
                    ...prev,
                    body_text: templateHtmlToText(prev.body_html),
                  }));
                }}
              >
                Refresh from message
              </button>
            </details>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Preview
              </div>
              <div className="text-sm text-slate-700">
                Subject: {form.subject || "--"}
              </div>
              <SafeHtml
                className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600"
                html={form.body_html || ""}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">CTA label</label>
              <input
                type="text"
                value={form.cta_label}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, cta_label: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">CTA URL</label>
              <input
                type="text"
                value={form.cta_url}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, cta_url: event.target.value }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">
                Attribution window (days){" "}
                <InfoTooltip text={CAMPAIGN_TOOLTIPS.attribution} label="Attribution" />
              </label>
              <input
                type="number"
                min="1"
                value={form.attribution_window_days}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    attribution_window_days: event.target.value,
                  }))
                }
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.include_promo_code}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    include_promo_code: event.target.checked,
                  }))
                }
              />
              Include promo code snippet
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.include_share_snippet}
                disabled={isPreviewMode}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    include_share_snippet: event.target.checked,
                  }))
                }
              />
              Include share to social snippet
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
              >
                Cancel
              </button>
              {isPreviewMode ? (
                <button
                  type="button"
                  onClick={() => openCustomize(editing)}
                  className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white"
                >
                  Customize
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSave}
                  className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                  disabled={saving}
                >
                  {saveButtonLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </SBINModal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete template"
        message="Delete this template? This cannot be undone."
        confirmingLabel="Deleting..."
        isSubmitting={saving}
      />
    </div>
  );
}
